/**
 * หน้าผลการบันทึก
 *
 * ผู้ใช้มาถึงหน้านี้ทันทีที่กดส่ง โดยที่ข้อมูลอาจยังไม่ถึงเซิร์ฟเวอร์
 * หน้านี้จึงติดตามสถานะจากคิวแบบสด และบอกให้ชัดว่าตอนนี้อยู่ขั้นไหน
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getQueued, QUEUE_STATUS_LABELS_TH, type QueuedCapture } from '../lib/db';
import { subscribeQueue, retryNow } from '../lib/queue';
import { formatDateTimeTh } from '../../shared/time';
import { metres } from '../lib/format';

export function ResultRoute() {
  const { queueId } = useParams<{ queueId: string }>();
  const [item, setItem] = useState<QueuedCapture | null | undefined>(undefined);

  useEffect(() => {
    if (!queueId) return;
    const load = () => void getQueued(queueId).then((found) => setItem(found ?? null));
    load();
    return subscribeQueue(load);
  }, [queueId]);

  if (item === undefined) {
    return (
      <div className="wrap">
        <div className="empty">
          <span className="spinner" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  if (item === null) {
    return (
      <div className="wrap stack">
        <div className="empty">ไม่พบรายการนี้ในคิว อาจถูกส่งสำเร็จและล้างออกไปแล้ว</div>
        <Link to="/my" className="btn btn-block">
          ไปที่รายการของฉัน
        </Link>
      </div>
    );
  }

  const sent = item.status === 'sent';
  const failed = item.status === 'failed';

  return (
    <div className="wrap stack-lg">
      <div className="card stack">
        <div className="row">
          <span
            className={
              sent
                ? 'pill pill-ok'
                : failed
                  ? 'pill pill-bad'
                  : item.status === 'sending'
                    ? 'pill pill-info'
                    : 'pill pill-warn'
            }
          >
            {QUEUE_STATUS_LABELS_TH[item.status]}
          </span>
          {item.status === 'sending' && <span className="spinner" aria-hidden="true" />}
        </div>

        <h1 className="card-title">
          {sent
            ? item.awaitingGpsApproval
              ? 'ส่งแล้ว รออนุมัติจากหัวหน้างาน'
              : 'บันทึกสำเร็จ'
            : failed
              ? 'ยังส่งไม่สำเร็จ'
              : 'บันทึกลงเครื่องแล้ว กำลังส่ง'}
        </h1>

        <p className="card-sub">
          {sent
            ? item.awaitingGpsApproval
              ? 'ข้อมูลและรูปถึงเซิร์ฟเวอร์แล้ว รายการจะสมบูรณ์เมื่อหัวหน้างานอนุมัติพิกัด'
              : 'ข้อมูล พิกัด และรูปถูกบันทึกเรียบร้อยแล้ว'
            : failed
              ? (item.lastErrorMessage ?? 'ระบบจะลองส่งใหม่ให้อัตโนมัติ')
              : 'ข้อมูลอยู่ในเครื่องแล้ว ปิดแอปได้เลย ระบบจะส่งให้เมื่อมีสัญญาณ'}
        </p>

        <div className="perf" />

        <dl className="kv">
          <div>
            <dt>เลขทะเบียน</dt>
            <dd>{item.customerRegistration}</dd>
          </div>
          {item.captureId && (
            <div>
              <dt>รหัสรายการ</dt>
              <dd>{item.captureId}</dd>
            </div>
          )}
          <div>
            <dt>ความแม่นยำ</dt>
            <dd>{metres(item.gps.accuracyM)}</dd>
          </div>
          <div>
            <dt>เวลาบันทึก</dt>
            <dd>{formatDateTimeTh(item.createdAt)}</dd>
          </div>
          <div>
            <dt>ความคืบหน้า</dt>
            <dd>
              {item.captureId ? '1' : '0'}/3 · {item.uploaded ? 'ส่งรูปแล้ว' : 'ยังไม่ส่งรูป'}
            </dd>
          </div>
          {item.attempts > 0 && (
            <div>
              <dt>ลองส่งแล้ว</dt>
              <dd>{item.attempts} ครั้ง</dd>
            </div>
          )}
        </dl>

        {!sent && (
          <div className="banner banner-warn">
            <span>อย่าล้างข้อมูลเบราว์เซอร์จนกว่ารายการนี้จะส่งสำเร็จ</span>
          </div>
        )}
      </div>

      <div className="actionbar">
        <Link to="/capture" className="btn btn-block">
          บันทึกรายการถัดไป
        </Link>
        {failed && (
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={() => void retryNow(item.id)}
          >
            ลองส่งใหม่ตอนนี้
          </button>
        )}
        <Link to="/my" className="btn btn-secondary btn-block">
          ดูรายการของฉัน
        </Link>
      </div>
    </div>
  );
}
